import { cn } from "@/utils/classnames"
import { getHex } from "@/theme/colors"
import { Text } from "@/elements"
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from "@heroicons/react/24/outline"

const Icon: React.FC<{
  open: boolean
  active: boolean
  onRemoveAll?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
}> = ({ onRemoveAll, active, open }) => {
  return (
    <>
      {active && !!onRemoveAll ? (
        <div className="pl-2" onClick={onRemoveAll}>
          <div className="flex size-[20px] items-center justify-center rounded-full bg-bg-6">
            <XMarkIcon height={14} stroke={getHex("bg-1")} />
          </div>
        </div>
      ) : (
        <div className="ml-1 flex size-[20px] items-center justify-center px-2">
          {open ? (
            <ChevronUpIcon height={18} className="flex-shrink-0 group-hover:stroke-fg-1" />
          ) : (
            <ChevronDownIcon height={18} className="flex-shrink-0 group-hover:stroke-fg-1" />
          )}
        </div>
      )}
    </>
  )
}

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
  onClick: () => void
  active: boolean
  hasDropdown?: boolean
  size?: "sm" | "md"
  open?: boolean
  showXIcon?: boolean
  count?: number | null | undefined
  onRemoveAll?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  textClassName?: string
}

export const FilterButton: React.FC<Props> = ({
  onClick,
  onRemoveAll,
  label,
  open = false,
  active,
  size = "md",
  showXIcon = true,
  className,
  count,
  hasDropdown = true,
  textClassName,
  ...rest
}) => {
  const handleRemoveAll = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (onRemoveAll) {
      e.preventDefault()
      e.stopPropagation()
      onRemoveAll(e)
    }
  }

  return (
    <button
      onClick={onClick}
      className={cn(
        active ? "border-fg-1 bg-fg-1 text-bg-1" : "border-bg-3 bg-bg-1",
        "duration-250ms group inline-flex items-center justify-center rounded-full border pl-4 transition-colors ease-in-out hover:!border-bg-3 hover:!bg-bg-3 hover:!text-fg-1",
        hasDropdown ? "pr-3" : "pr-4",
        size === "sm" ? "h-[34px]" : "h-[40px]",
        className
      )}
      {...rest}>
      <Text variant="body1" className={textClassName}>
        {label}
      </Text>
      {count && (
        <Text variant="CTASmall" className="ml-1 text-primary-3">
          {count}
        </Text>
      )}
      {hasDropdown && <Icon active={active && showXIcon} open={open} onRemoveAll={handleRemoveAll} />}
    </button>
  )
}
